/**
 * Field Mappings and Standardization Utilities
 * 
 * Canonical field definitions for the PodGuestly application.
 * All data is stored using these standardized field names.
 * 
 * Standardized Schema:
 * - firstName, lastName (person names)
 * - companyName (company identifier)
 * - createdAt, updatedAt (timestamps)
 * - status: lowercase values (new, contacted, responded, scheduled, declined)
 */

// Status value definitions (lowercase only)
export const STATUS_VALUES = {
    NEW: 'new',
    CONTACTED: 'contacted',
    RESPONDED: 'responded',
    SCHEDULED: 'scheduled',
    DECLINED: 'declined',
    IDENTIFIED: 'identified',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PENDING: 'pending',
    COMPLETED: 'completed',
    PREPPED: 'prepped',
    RECORDED: 'recorded',
    PUBLISHED: 'published',
    POST_PRODUCTION: 'post-production'
};

// Status display mappings (for UI)
export const STATUS_DISPLAY = {
    'new': 'New',
    'contacted': 'Contacted',
    'responded': 'Responded',
    'scheduled': 'Scheduled',
    'declined': 'Declined',
    'identified': 'Identified',
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'completed': 'Completed',
    'prepped': 'Prepped',
    'recorded': 'Recorded',
    'published': 'Published',
    'post-production': 'Post-Production'
};

/**
 * Prepare a lead object for Firestore storage
 * Ensures all required fields are present with correct formatting
 * @param {Object} data - The data to prepare
 * @returns {Object} Data with standardized fields
 */
export const prepareForStorage = (data) => {
    const prepared = { ...data };

    // Name fields - derive firstName/lastName from various inputs
    if (data.firstName || data.lastName) {
        // Already have standardized fields
        prepared.firstName = data.firstName || '';
        prepared.lastName = data.lastName || '';
    } else if (data.founderName || data.guestName || data.name) {
        // Split full name into parts
        const fullName = data.founderName || data.guestName || data.name;
        const parts = fullName.trim().split(/\s+/);
        prepared.firstName = parts[0] || '';
        prepared.lastName = parts.slice(1).join(' ') || '';
    }

    // Company field
    prepared.companyName = data.companyName || data.company || data.guestCompany || '';

    // Bio field
    prepared.guestBio = (data.guestBio || data.bio || data.notes || '').trim();

    // Status - normalize to lowercase
    if (data.status) {
        prepared.status = getStatusForStorage(data.status);
    } else {
        prepared.status = STATUS_VALUES.NEW;
    }

    // Timestamps
    if (!prepared.createdAt) {
        prepared.createdAt = new Date().toISOString();
    }
    prepared.updatedAt = new Date().toISOString();

    return prepared;
};

/**
 * Get display-friendly status value
 * @param {string} status - The status value
 * @returns {string} Title Case status for display
 */
export const getStatusDisplay = (status) => {
    if (!status) return 'New';
    return STATUS_DISPLAY[status.toLowerCase()] || status;
};

/**
 * Get standardized status value for storage
 * @param {string} status - The status value (can be lowercase or Title Case)
 * @returns {string} Lowercase status for storage
 */
export const getStatusForStorage = (status) => {
    if (!status) return STATUS_VALUES.NEW;
    const normalized = status.toLowerCase().replace(/\s+/g, '-');
    return STATUS_DISPLAY[normalized] ? normalized : status.toLowerCase();
};

/**
 * Get full name from a lead object
 * @param {Object} lead - The lead object
 * @returns {string} The full name
 */
export const getFullName = (lead) => {
    if (!lead) return '';

    if (lead.firstName || lead.lastName) {
        return [lead.firstName, lead.lastName].filter(Boolean).join(' ');
    }

    return '';
};

/**
 * Get company name from a lead object
 * @param {Object} lead - The lead object
 * @returns {string} The company name
 */
export const getCompanyName = (lead) => {
    if (!lead) return '';

    const company = lead.companyName || '';

    if (typeof company === 'object' && company !== null) {
        return company.name || '';
    }

    return company;
};

export default {
    STATUS_VALUES,
    STATUS_DISPLAY,
    prepareForStorage,
    getStatusDisplay,
    getStatusForStorage,
    getFullName,
    getCompanyName
};
